import React, { useState, useMemo } from "react";
import { DndContext, DragOverEvent, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox } from "@mui/material";

interface Props {
  initialControls: any[];
  onApply: (visibleControls: any[], hiddenControls: any[]) => void;
  /** 보이는 채로 체크를 풀 수 없는 칸 — 입력 폼의 필수 항목. 안 넘기면 전부 풀 수 있다 (2026-10-06) */
  isLocked?: (control: any) => boolean;
}

/** 보이는 이름 — 라벨, 없으면 파라미터명 (2026-10-02) */
const controlLabel = (col: any): string =>
  String(col?.nm_label || "").trim() || String(col?.cd_param || "").trim();

export const SnControlSelector: React.FC<Props> = ({
  initialControls,
  onApply,
  isLocked,
}) => {
  const [controls, setControls] = useState(initialControls);
  const [search, setSearch] = useState("");

  /* 라벨이 없는 칸(기간콤보처럼 앞의 콤보가 라벨 역할인 칸)도 있다 — 라벨이 없으면 파라미터명으로 보이고 검색한다.
     전에는 라벨이 null 인 칸에서 toLowerCase 오류로 팝업이 열리지 않았다 (2026-10-02) */
  const filtered = useMemo(() => {
    const keyword = search.toLowerCase();
    return controls.filter((col) => controlLabel(col).toLowerCase().includes(keyword));
  }, [search, controls]);

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!active || !over || active.id === over.id) return;

    setControls((prevcontrols) => {
      const oldIndex = prevcontrols.findIndex(
        (col) => col.no_line === active.id
      );
      const newIndex = prevcontrols.findIndex((col) => col.no_line === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const updatedControls = arrayMove(prevcontrols, oldIndex, newIndex);
        const visibleControls = updatedControls.filter(
          (col) => col.fg_visible === "Y"
        );
        const hiddenControls = updatedControls.filter(
          (col) => col.fg_visible !== "Y"
        );
        onApply(visibleControls, hiddenControls);
        return updatedControls;
      }
      // Split controls by fg_visible and call onApply

      return prevcontrols;
    });
  };

  const toggleVisibility = (no_line: string) => {
    setControls((prev) => {
      const updated = prev.map((col) =>
        col.no_line === no_line
          ? { ...col, fg_visible: col.fg_visible == "Y" ? "N" : "Y" }
          : col
      );
      const visibleControls = updated.filter((col) => col.fg_visible === "Y");
      const hiddenControls = updated.filter((col) => col.fg_visible !== "Y");
      onApply(visibleControls, hiddenControls);
      return updated;
    });
  };

  return (
    <div
      style={{ width: 175, height: 400 }}
      //className="border rounded shadow bg-white p-2"
    >
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search..."
        className="border px-2 py-1 mb-2 w-full rounded text-sm"
      />
      <DndContext
        collisionDetection={closestCenter}
        onDragOver={handleDragOver}
      >
        <SortableContext
          items={filtered}
          strategy={verticalListSortingStrategy}
        >
          {filtered.map((col) => (
            <SortableItem
              key={col.no_line}
              control={col}
              locked={col.fg_visible == "Y" && !!isLocked?.(col)}
              onToggle={toggleVisibility}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

interface SortableItemProps {
  control: any;
  /** 체크를 풀 수 없다 — 체크된 채로 흐리게 보인다 */
  locked?: boolean;
  onToggle: (id: string) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ control, locked = false, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: control.no_line });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div className="flex justify-left">
      <div
        style={{
          width: "20px",
          height: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <label className="ag-checkbox" style={{ margin: 0 }}>
          <Checkbox
            className="ag-checkbox-input"
            checked={control.fg_visible == "Y"}
            disabled={locked}
            title={locked ? "필수 항목입니다." : undefined}
            onChange={() => {
              onToggle(control.no_line);
            }}
            sx={{
              height: "30px",
              "& .MuiSvgIcon-root": {
                fontSize: 22,
              },
              alignContent: "center",
              color: "#7d7e80",
            }}
          />
        </label>
      </div>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className="flex items-center gap-2 px-2 py-1 text-sm"
      >
        <div className="cursor-move">⋮⋮</div>
        <span>{controlLabel(control)}</span>
      </div>
    </div>
  );
};
